import { useRef, useState, type ChangeEvent, type DragEvent, type MouseEvent } from 'react'
import type { EditorAttachmentState } from '@/msgdeskRuntime/state'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { useEditorAttachments, visibleEditorAttachments } from '@/deskUiSkeletons/editor/useEditorAttachments'
import { MailboxIcon } from '../MailboxIcons'

function attachmentStatus(attachment: EditorAttachmentState) {
  if (attachment.status === 'uploading') return attachment.progress || 'Uploading'
  if (attachment.error) return attachment.error
  return attachment.active === false ? 'Removed' : 'Attached'
}

function AttachmentRow({ attachment }: { attachment: EditorAttachmentState }) {
  const active = attachment.active !== false

  function handleToggle(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    event.stopPropagation()
    dispatchEditorAction({ action: 'toggle-attachment', attachment, active: !active })
  }

  return (
    <div className={`flex min-w-0 items-center gap-2 rounded border px-2.5 py-2 ${active ? 'border-[#d9e1e8] bg-white' : 'border-[#e2e7ec] bg-[#f5f7f9] opacity-65'} ${attachment.status === 'error' ? '!border-[#d99a9a] !bg-[#fff8f8]' : ''}`}>
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded bg-[#edf3fb] text-[#2563eb]">
        <MailboxIcon name="attachment" className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12px] font-medium text-[#26323d]" title={attachment.filename || 'Attachment'}>{attachment.filename || 'Attachment'}</div>
        <div className={`truncate text-[10px] ${attachment.error ? 'text-[#a13d3d]' : 'text-[#778593]'}`}>{attachmentStatus(attachment)}</div>
      </div>
      {(attachment.removable || (!active && attachment.toggleable)) ? (
        <button
          type="button"
          className="flex h-7 flex-none items-center gap-1 rounded px-2 text-[11px] font-medium text-[#526272] hover:bg-[#edf2f6]"
          onClick={handleToggle}
          title={active ? 'Remove attachment' : 'Restore attachment'}
        >
          {active ? <MailboxIcon name="trash" className="h-3.5 w-3.5" /> : <MailboxIcon name="plus" className="h-3.5 w-3.5" />}
          {active ? 'Remove' : 'Restore'}
        </button>
      ) : null}
    </div>
  )
}

export function MailboxEditorAttachments({ editorMode }: { editorMode?: EditorMode }) {
  const attachments = visibleEditorAttachments(useEditorAttachments(), editorMode)
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)

  async function uploadFiles(files: FileList | File[]) {
    await Promise.all(Array.from(files).map(file => dispatchEditorAction({ action: 'upload-attachment', file })))
  }

  async function handleInput(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.currentTarget.files || [])
    event.currentTarget.value = ''
    if (files.length) await uploadFiles(files)
  }

  function handleDragOver(event: DragEvent<HTMLElement>) {
    event.preventDefault()
    setDragActive(true)
  }

  function handleDragLeave(event: DragEvent<HTMLElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragActive(false)
  }

  async function handleDrop(event: DragEvent<HTMLElement>) {
    event.preventDefault()
    setDragActive(false)
    if (event.dataTransfer.files.length) await uploadFiles(event.dataTransfer.files)
  }

  return (
    <section
      className={`border-b px-5 py-2.5 ${dragActive ? 'border-[#7ea7df] bg-[#eef5ff]' : 'border-[#d9e1e8] bg-[#f8fafb]'}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="flex h-8 items-center gap-2 rounded border border-[#c8d2dc] bg-white px-3 text-[12px] font-medium text-[#314253] shadow-sm hover:bg-[#edf2f6]"
          onClick={() => inputRef.current?.click()}
        >
          <MailboxIcon name="attachment" className="h-4 w-4" />
          Add files
        </button>
        <span className="text-[11px] text-[#778593]">Drop files here or choose from your computer</span>
        {attachments.length ? <span className="ml-auto text-[11px] tabular-nums text-[#687684]">{attachments.filter(item => item.active !== false).length} attached</span> : null}
      </div>
      {attachments.length ? (
        <div className="mt-2 grid grid-cols-2 gap-2 max-[900px]:grid-cols-1">
          {attachments.map((attachment, index) => <AttachmentRow key={attachment.id || `attachment-${index}`} attachment={attachment} />)}
        </div>
      ) : null}
      <input ref={inputRef} type="file" multiple className="hidden" onChange={handleInput} />
    </section>
  )
}
